import { useEffect, useRef, useState } from 'react';
import { Network } from '@capacitor/network';
import { isNativeApp } from '@/lib/nativeApp';
import { forceRealtimeReconnect } from '@/lib/realtimeReconnect';

export interface NetworkStatus {
  connected: boolean;
  connectionType: string;
}

export const useNetworkStatus = (): NetworkStatus => {
  const [status, setStatus] = useState<NetworkStatus>({
    connected: true,
    connectionType: 'unknown',
  });
  const prevConnectedRef = useRef<boolean>(true);

  useEffect(() => {
    // Get initial status
    const getInitialStatus = async () => {
      try {
        if (isNativeApp()) {
          const networkStatus = await Network.getStatus();
          setStatus({
            connected: networkStatus.connected,
            connectionType: networkStatus.connectionType,
          });
        } else {
          // Web fallback
          setStatus(prev => ({ ...prev, connected: navigator.onLine }));
        }
      } catch { /* non-fatal */ }
    };

    getInitialStatus();

    if (isNativeApp()) {
      // Native: use Capacitor Network plugin
      const listenerPromise = Network.addListener('networkStatusChange', networkStatus => {
        setStatus({
          connected: networkStatus.connected,
          connectionType: networkStatus.connectionType,
        });
        // Offline→online transition: the old websocket is dead but phoenix may
        // not know yet — force a hard realtime reconnect.
        if (networkStatus.connected && !prevConnectedRef.current) {
          forceRealtimeReconnect('network-regained');
        }
        prevConnectedRef.current = networkStatus.connected;
      });
      return () => { listenerPromise.then(l => l.remove()); };
    } else {
      // Web fallback: browser online/offline events
      const handleOnline = () => {
        setStatus(prev => ({ ...prev, connected: true }));
        if (!prevConnectedRef.current) {
          forceRealtimeReconnect('network-regained');
        }
        prevConnectedRef.current = true;
      };
      const handleOffline = () => {
        setStatus(prev => ({ ...prev, connected: false }));
        prevConnectedRef.current = false;
      };
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return status;
};

export default useNetworkStatus;
